import type { AuditLogEntry } from '@rossum/api-client/auditLogs';
import type { ActivitySource, ActorRef } from '../model';

const nonEmpty = (value: string | null | undefined): string | null =>
  value || null;

export const toActivitySource = (entry: AuditLogEntry): ActivitySource => {
  const { username, sourceType, sourceDetails } = entry;

  const user = nonEmpty(username) ?? nonEmpty(sourceDetails?.userEmail);

  switch (sourceType) {
    case 'user':
      return { actor: user, sourceType };
    case 'hook':
      return {
        actor: nonEmpty(sourceDetails?.hookName),
        sourceType,
        ...(sourceDetails?.hookId ? { hookId: sourceDetails.hookId } : {}),
      };
    case 'rules':
      return {
        actor: nonEmpty(
          (sourceDetails?.rules ?? [])
            .flatMap(rule => (rule.ruleName ? [rule.ruleName] : []))
            .join(', ')
        ),
        sourceType,
      };
    case 'email':
      return { actor: nonEmpty(sourceDetails?.emailSender), sourceType };
    case 'system':
    case 'formula_fields':
      return { actor: null, sourceType };
    default:
      return { actor: user, sourceType: null };
  }
};

// An unattributed entry that still names a person is shown under their name,
// so it counts as their change.
export const isManualSource = ({ actor, sourceType }: ActivitySource) =>
  sourceType === 'user' || (sourceType === null && actor !== null);

type ActorRefWithNameKey = { ref: ActorRef; nameKey: string };

const actorRef = (
  kind: 'user' | 'hook' | 'rule' | 'email',
  id: number | null | undefined,
  name: string | null
): ActorRefWithNameKey[] => {
  if (!name) return [];
  const nameKey = `${kind}:${name}`;
  return [
    {
      ref: {
        id: id === null || id === undefined ? nameKey : `${kind}:${id}`,
        name,
      },
      nameKey,
    },
  ];
};

const toActorRefsWithNameKey = (
  entry: AuditLogEntry
): ActorRefWithNameKey[] => {
  const { username, sourceType, sourceDetails } = entry;
  const user = nonEmpty(username) ?? nonEmpty(sourceDetails?.userEmail);

  switch (sourceType) {
    case 'user':
    case null:
      return actorRef('user', sourceDetails?.userId, user);
    case 'hook':
      return actorRef(
        'hook',
        sourceDetails?.hookId,
        nonEmpty(sourceDetails?.hookName)
      );
    case 'rules':
      return (sourceDetails?.rules ?? []).flatMap(rule =>
        actorRef('rule', rule.ruleId, nonEmpty(rule.ruleName))
      );
    case 'email':
      return actorRef('email', null, nonEmpty(sourceDetails?.emailSender));
    default:
      return [];
  }
};

export const toActorRefs = (entry: AuditLogEntry): ActorRef[] =>
  toActorRefsWithNameKey(entry).map(({ ref }) => ref);

export const toActorResolver = (
  entries: AuditLogEntry[]
): ((ref: ActorRef) => ActorRef) => {
  const idByName = new Map(
    entries
      .flatMap(toActorRefsWithNameKey)
      .filter(({ ref, nameKey }) => ref.id !== nameKey)
      .map(({ ref, nameKey }) => [nameKey, ref.id])
  );
  return ref => ({ ...ref, id: idByName.get(ref.id) ?? ref.id });
};
